/* eslint-disable unicorn/prefer-includes */

/* eslint-disable unicorn/prefer-string-slice */
import { useMemo, useRef, useState } from 'react'

import {
  FormatInputValueFunction,
  NumberFormatBaseProps,
  OnValueChange,
} from './types'

// eslint-disable-next-line @typescript-eslint/no-empty-function
export function noop() {}

export function charIsNumber(char?: string) {
  return /\d/.test(char || '')
}

export function charIsString(char?: string) {
  return /[A-Za-zА-я]/.test(char || '')
}

export function charIsAny(char?: string) {
  return /[\dA-Za-zА-я]/.test(char || '')
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isNil(val: any): val is null | undefined {
  return val === null || val === undefined
}

export function isNanValue(val: string | number) {
  return typeof val === 'number' && Number.isNaN(val)
}

export function escapeRegExp(str: string) {
  return str.replace(/[$()*+./?[\\\]^{|}-]/g, '\\$&')
}

export function getThousandsGroupRegex(thousandsGroupStyle: string) {
  switch (thousandsGroupStyle) {
    case 'lakh': {
      return /(\d+?)(?=(\d\d)+(\d)(?!\d))(\.\d+)?/g
    }
    case 'wan': {
      return /(\d)(?=(\d{4})+(?!\d))/g
    }
    case 'thousand': {
      return /(\d)(?=(\d{3})+(?!\d))/g
    }
    default: {
      return /(\d)(?=(\d{3})+(?!\d))/g
    }
  }
}

export function applyThousandSeparator(
  str: string,
  thousandSeparator: string,
  thousandsGroupStyle: string,
) {
  const thousandsGroupRegex = getThousandsGroupRegex(thousandsGroupStyle)
  let index = str.search(/[1-9]/)
  index = index === -1 ? str.length : index
  return (
    str.substring(0, index) +
    str
      .substring(index, str.length)
      .replace(thousandsGroupRegex, '$1' + thousandSeparator)
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function usePersistentCallback<T extends (...args: any[]) => any>(
  cb: T,
) {
  const callbackRef = useRef<T>(cb)
  callbackRef.current = cb
  type Params = Parameters<T>

  const persistentCbRef = useRef(function (...args: Params) {
    return callbackRef.current(...args)
  } as T)

  return persistentCbRef.current
}

export function splitDecimal(numStr: string, allowNegative = true) {
  const hasNegation = numStr[0] === '-'
  const addNegation = hasNegation && allowNegative
  numStr = numStr.replace('-', '')

  const parts = numStr.split('.')
  const beforeDecimal = parts[0]
  const afterDecimal = parts[1] || ''

  return {
    beforeDecimal,
    afterDecimal,
    hasNegation,
    addNegation,
  }
}

export function fixLeadingZero(numStr?: string) {
  if (!numStr) return numStr
  const isNegative = numStr[0] === '-'
  if (isNegative) numStr = numStr.substring(1, numStr.length)
  const parts = numStr.split('.')
  const beforeDecimal = parts[0].replace(/^0+/, '') || '0'
  const afterDecimal = parts[1] || ''

  // eslint-disable-next-line sonarjs/no-nested-template-literals
  return `${isNegative ? '-' : ''}${beforeDecimal}${
    afterDecimal ? `.${afterDecimal}` : ''
  }`
}

export function limitToScale(
  numStr: string,
  scale: number,
  fixedDecimalScale: boolean,
) {
  let str = ''
  const filler = fixedDecimalScale ? '0' : ''
  for (let i = 0; i <= scale - 1; i++) {
    str += numStr[i] || filler
  }
  return str
}

function repeat(str: string, count: number) {
  // eslint-disable-next-line unicorn/new-for-builtins
  return Array(count + 1).join(str)
}

export function toNumericString(num: string | number) {
  let _num = num + '' // typecast number to string

  // store the sign and remove it from the number.
  const sign = _num[0] === '-' ? '-' : ''
  if (sign) _num = _num.substring(1)

  let [coefficient, exponent] = _num.split(/[Ee]/g) as [
    string,
    string | number | undefined,
  ]

  exponent = Number(exponent)

  if (!exponent) return sign + coefficient

  coefficient = coefficient.replace('.', '')

  const decimalIndex = 1 + exponent

  const coffiecientLn = coefficient.length

  if (decimalIndex < 0) {
    coefficient = '0.' + repeat('0', Math.abs(decimalIndex)) + coefficient
  } else if (decimalIndex >= coffiecientLn) {
    coefficient = coefficient + repeat('0', decimalIndex - coffiecientLn)
  } else {
    coefficient =
      (coefficient.substring(0, decimalIndex) || '0') +
      '.' +
      coefficient.substring(decimalIndex)
  }

  return sign + coefficient
}

export function roundToPrecision(
  numStr: string,
  scale: number,
  fixedDecimalScale: boolean,
) {
  if (['', '-'].indexOf(numStr) !== -1) return numStr

  const shouldHaveDecimalSeparator =
    (numStr.indexOf('.') !== -1 || fixedDecimalScale) && scale
  const { beforeDecimal, afterDecimal, hasNegation } = splitDecimal(numStr)
  const floatValue = Number.parseFloat(`0.${afterDecimal || '0'}`)
  const floatValueStr =
    afterDecimal.length <= scale
      ? `0.${afterDecimal}`
      : floatValue.toFixed(scale)
  const roundedDecimalParts = floatValueStr.split('.')
  const intPart = [...beforeDecimal]
    .reverse()
    .reduce((roundedStr, current, idx) => {
      if (roundedStr.length > idx) {
        return (
          (Number(roundedStr[0]) + Number(current)).toString() +
          roundedStr.substring(1, roundedStr.length)
        )
      }
      return current + roundedStr
    }, roundedDecimalParts[0])

  const decimalPart = limitToScale(
    roundedDecimalParts[1] || '',
    scale,
    fixedDecimalScale,
  )
  const negation = hasNegation ? '-' : ''
  const decimalSeparator = shouldHaveDecimalSeparator ? '.' : ''
  return `${negation}${intPart}${decimalSeparator}${decimalPart}`
}

export function setCaretPosition(
  el: HTMLInputElement | HTMLTextAreaElement,
  caretPos: number,
) {
  // eslint-disable-next-line no-self-assign
  el.value = el.value
  // (it causes an issue in chrome, and having it doesn't hurt any other browser)
  if (el !== null) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    /* @ts-ignore */
    if (el.createTextRange) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      /* @ts-ignore */
      const range = el.createTextRange()
      range.move('character', caretPos)
      range.select()
      return true
    }
    if (el.selectionStart || el.selectionStart === 0) {
      el.focus()
      el.setSelectionRange(caretPos, caretPos)
      return true
    }

    el.focus()
    return false
  }
}

export function findChangeRange(prevValue: string, newValue: string) {
  let i = 0,
    j = 0
  const prevLength = prevValue.length
  const newLength = newValue.length
  while (prevValue[i] === newValue[i] && i < prevLength) i++

  while (
    prevValue[prevLength - 1 - j] === newValue[newLength - 1 - j] &&
    newLength - j > i &&
    prevLength - j > i
  ) {
    j++
  }

  return {
    from: { start: i, end: prevLength - j },
    to: { start: i, end: newLength - j },
  }
}

export function clamp(num: number, min: number, max: number) {
  return Math.min(Math.max(num, min), max)
}

export function geInputCaretPosition(
  el: HTMLInputElement | HTMLTextAreaElement,
) {
  return Math.max(el.selectionStart as number, el.selectionEnd as number)
}

export function getDefaultChangeMeta(value: string) {
  return {
    from: {
      start: 0,
      end: 0,
    },
    to: {
      start: 0,
      end: value.length,
    },
    lastValue: '',
  }
}

export function getMaskAtIndex(mask: string | string[] = ' ', index: number) {
  if (typeof mask === 'string') {
    return mask
  }

  return mask[index] || ' '
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function getCaretPosition(
  newFormattedValue: string,
  lastFormattedValue: string,
  curValue: string,
  curCaretPos: number,
  boundary: boolean[],
  isValidInputCharacter: (char: string) => boolean,
) {
  // eslint-disable-next-line unicorn/prefer-native-coercion-functions
  const firstAllowedPosition = boundary.findIndex((b) => b)
  const prefixFormat = newFormattedValue.slice(0, firstAllowedPosition)
  if (!lastFormattedValue && !curValue.startsWith(prefixFormat)) {
    curValue = prefixFormat + curValue
    curCaretPos = curCaretPos + prefixFormat.length
  }

  const curValLn = curValue.length
  const formattedValueLn = newFormattedValue.length

  const addedIndexMap: { [key: number]: boolean } = {}
  // eslint-disable-next-line unicorn/no-new-array
  const indexMap = new Array(curValLn)

  for (let i = 0; i < curValLn; i++) {
    indexMap[i] = -1
    for (let j = 0, jLn = formattedValueLn; j < jLn; j++) {
      if (curValue[i] === newFormattedValue[j] && !addedIndexMap[j]) {
        indexMap[i] = j
        addedIndexMap[j] = true
        break
      }
    }
  }

  let pos = curCaretPos
  while (
    pos < curValLn &&
    (indexMap[pos] === -1 || !isValidInputCharacter(curValue[pos]))
  ) {
    pos++
  }

  const endIndex =
    pos === curValLn || indexMap[pos] === -1 ? formattedValueLn : indexMap[pos]

  pos = curCaretPos - 1
  while (pos > 0 && indexMap[pos] === -1) pos--
  const startIndex = pos === -1 || indexMap[pos] === -1 ? 0 : indexMap[pos] + 1

  if (startIndex > endIndex) return endIndex

  return curCaretPos - startIndex < endIndex - curCaretPos
    ? startIndex
    : endIndex
}

export function getCaretPosInBoundary(
  value: string,
  caretPos: number,
  boundary: boolean[],
  direction?: string,
) {
  const valLn = value.length

  caretPos = clamp(caretPos, 0, valLn)

  if (direction === 'left') {
    while (caretPos >= 0 && !boundary[caretPos]) caretPos--

    if (caretPos === -1) caretPos = boundary.indexOf(true)
  } else {
    while (caretPos <= valLn && !boundary[caretPos]) caretPos++

    if (caretPos > valLn) caretPos = boundary.lastIndexOf(true)
  }

  if (caretPos === -1) caretPos = valLn

  return caretPos
}

export function caretUnknownFormatBoundary(formattedValue: string) {
  const boundaryAry = Array.from({ length: formattedValue.length + 1 }).map(
    () => true,
  )

  for (let i = 0, ln = boundaryAry.length; i < ln; i++) {
    boundaryAry[i] = Boolean(
      charIsNumber(formattedValue[i]) || charIsNumber(formattedValue[i - 1]),
    )
  }

  return boundaryAry
}

export function useInternalValues(
  value: string | number | null | undefined,
  defaultValue: string | number | null | undefined,
  valueIsNumericString: boolean,
  format: FormatInputValueFunction,
  removeFormatting: NumberFormatBaseProps['removeFormatting'],
  onValueChange: NumberFormatBaseProps['onValueChange'] = noop,
): [{ formattedValue: string; numAsString: string }, OnValueChange] {
  type Values = { formattedValue: string; numAsString: string }

  const propValues = useRef<Values>()

  const getValues = usePersistentCallback(
    (value: string | number | null | undefined) => {
      let formattedValue, numAsString
      if (isNil(value) || isNanValue(value)) {
        numAsString = ''
        formattedValue = ''
      } else if (typeof value === 'number' || valueIsNumericString) {
        numAsString = typeof value === 'number' ? toNumericString(value) : value
        formattedValue = format(numAsString)
      } else {
        numAsString = removeFormatting
          ? // eslint-disable-next-line unicorn/no-useless-undefined
            removeFormatting(value, undefined)
          : value
        formattedValue = value
      }

      return { formattedValue, numAsString }
    },
  )

  const [values, setValues] = useState<Values>(() => {
    return getValues(defaultValue)
  })

  const _onValueChange: typeof onValueChange = (values, sourceInfo) => {
    setValues({
      formattedValue: values.formattedValue,
      numAsString: values.value,
    })

    onValueChange(values, sourceInfo)
  }

  useMemo(() => {
    if (isNil(value)) {
      propValues.current = undefined
    } else {
      propValues.current = getValues(value)
      setValues(propValues.current)
    }
  }, [value, getValues])

  return [values, _onValueChange]
}
